import React from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiFileDocumentOutline,
  mdiImport,
  mdiViewDashboardOutline,
  mdiArrowRight,
} from '@mdi/js';

/* ────────────────────────── Styled Components ────────────────────────── */

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 249, 250, 0.97);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 720px;
  width: 100%;
  padding: 48px 32px;
`;

const BrandMark = styled.div`
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  font-size: 36px;
  color: #202D61;
  letter-spacing: 1px;
  line-height: 1;
`;

const Subtitle = styled.div`
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6d8dbf;
  margin-top: 6px;
  letter-spacing: 0.3px;
`;

const Description = styled.p`
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  color: #5f6368;
  text-align: center;
  line-height: 1.6;
  max-width: 520px;
  margin: 24px 0 36px;
`;

const TemplatesHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Sora', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #9aa0a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const TemplateRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
`;

const TemplateCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 210px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: 'Sora', sans-serif;

  &:hover {
    border-color: #202D61;
    box-shadow: 0 2px 12px rgba(32, 45, 97, 0.1);
  }

  &:active {
    box-shadow: 0 1px 4px rgba(32, 45, 97, 0.12);
  }

  &:focus-visible {
    outline: 2px solid #6d8dbf;
    outline-offset: 2px;
  }
`;

const CardIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  background: #f0f3f9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`;

const CardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const CardDescription = styled.div`
  font-size: 12px;
  color: #5f6368;
  line-height: 1.4;
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin: 32px 0;
  gap: 16px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #dadce0;
`;

const DividerText = styled.span`
  font-family: 'Sora', sans-serif;
  font-size: 12px;
  color: #9aa0a6;
  text-transform: lowercase;
`;

const BlankButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #202D61;
  background: transparent;
  border: 1px solid #202D61;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #202D61;
    color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid #6d8dbf;
    outline-offset: 2px;
  }
`;

const ImportLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 20px;
  padding: 4px 8px;
  font-family: 'Sora', sans-serif;
  font-size: 12px;
  color: #6d8dbf;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #202D61;
    background: #f0f3f9;
  }
`;

const ShortcutsHint = styled.div`
  margin-top: 32px;
  font-family: 'Sora', sans-serif;
  font-size: 11px;
  color: #bdc1c6;
  letter-spacing: 0.2px;

  & kbd {
    font-family: 'Sora', monospace;
    font-size: 11px;
    padding: 1px 5px;
    background: #f1f3f4;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    color: #5f6368;
  }
`;

/* ────────────────────────── Component ────────────────────────── */

interface WelcomeScreenProps {
  templates: { id: number; name: string; description: string }[];
  onSelectTemplate: (templateId: number) => void;
  onStartBlank: () => void;
  onImport: () => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  templates,
  onSelectTemplate,
  onStartBlank,
  onImport,
}) => {
  const visibleTemplates = templates.slice(0, 3);

  return (
    <Overlay>
      <Content>
        {/* Branding */}
        <BrandMark>BSPL</BrandMark>
        <Subtitle>Cooling Circuit Designer</Subtitle>

        <Description>
          Design and simulate 1D cooling circuits. Select a BSPL cooling package
          and verify it meets your thermal requirements.
        </Description>

        {/* Template cards */}
        {visibleTemplates.length > 0 && (
          <>
            <TemplatesHeading>
              <Icon path={mdiViewDashboardOutline} size={0.55} color="#9aa0a6" />
              Start from a template
            </TemplatesHeading>

            <TemplateRow>
              {visibleTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  onClick={() => onSelectTemplate(template.id)}
                >
                  <CardIconWrapper>
                    <Icon path={mdiFileDocumentOutline} size={0.65} color="#202D61" />
                  </CardIconWrapper>
                  <CardName>{template.name}</CardName>
                  <CardDescription>{template.description}</CardDescription>
                </TemplateCard>
              ))}
            </TemplateRow>
          </>
        )}

        {/* Divider */}
        <DividerRow>
          <DividerLine />
          <DividerText>or</DividerText>
          <DividerLine />
        </DividerRow>

        {/* Blank canvas */}
        <BlankButton onClick={onStartBlank}>
          Start from blank canvas
          <Icon path={mdiArrowRight} size={0.6} />
        </BlankButton>

        {/* Import link */}
        <ImportLink onClick={onImport}>
          <Icon path={mdiImport} size={0.5} />
          Import existing circuit (.xml)
        </ImportLink>

        {/* Keyboard shortcuts hint */}
        <ShortcutsHint>
          <kbd>Ctrl+Z</kbd> undo &middot; <kbd>Ctrl+C</kbd>/<kbd>V</kbd> copy/paste
        </ShortcutsHint>
      </Content>
    </Overlay>
  );
};
